import { Fix64, GearBonus, GearItem, GearOwner, GearStat, PlayerProfile, affinityOf, bonusFor, mythicActiveOn, primaryOn, primaryStatOn, sameOwner } from "@highmarch/domain";
import { itemScore } from "./gearPlan";

/**
 * "What does this item do on that owner?" (the Gear screen's compare, the chest reveal's "for Ballista", a Best move
 * row): the owner's sim bonus now against the same with the item on, both from the domain's own `bonusFor` over a
 * swapped copy of the inventory, so primaries (affinity included), affixes, the set bonus and Mythic effects count
 * exactly as they will in the level. Pure — nothing on the profile changes.
 */

/** One stat on the owner, before and after (fractions, e.g. 0.06 = +6 %). */
export interface StatDelta { readonly stat: GearStat; readonly before: Fix64; readonly after: Fix64 }

/** A Mythic effect (index into `gear.mythicEffects`) and whether it acts on the owner in question (`mythicActiveOn`). */
export interface MythicOn { readonly effect: number; readonly active: boolean }

export interface GearCompare {
  readonly item: GearItem;
  readonly owner: GearOwner;
  /** What the owner wears in the item's slot now (null: empty, or it is this item). */
  readonly worn: GearItem | null;
  /** The item is on this owner already (the compare then reads "worn vs nothing" as before = after). */
  readonly wearing: boolean;
  /** The profile's standing equip rule for this owner (`canWear`): true, or why not ("level too low", …). */
  readonly canWear: true | string;
  /** The owner level the item asks for when the owner is below it; null when it can wear it. */
  readonly needLevel: number | null;
  /** The owner's whole gear bonus now and with the item on. */
  readonly before: GearBonus;
  readonly after: GearBonus;
  /** All five stats on the owner, before/after (primary, affixes and set bonus together): the stat chips. */
  readonly stats: readonly StatDelta[];
  /** The item's primary on the owner against the worn item's (0 when empty); `affinity` when the owner answers best to it. */
  readonly primary: StatDelta & { readonly affinity: boolean };
  /** The Epic set bonus on the owner before and after: after && !before completes it, before && !after breaks it. */
  readonly setBefore: boolean;
  readonly setAfter: boolean;
  /** The item's Mythic effect on this owner (inactive: it does nothing there), and the worn item's that would go. */
  readonly mythic: MythicOn | null;
  readonly mythicLost: MythicOn | null;
  /** Gear power on the owner: itemScore(item) − itemScore(worn). */
  readonly power: number;
  /** The item is on another owner now: equipping takes it off there… */
  readonly takesFrom: GearOwner | null;
  /** …and that breaks the set bonus there. */
  readonly takesFromBreaksSet: boolean;
  /** The worn item goes to the stash. */
  readonly toStash: GearItem | null;
  /** Equipping binds the item (it is above the bind threshold and not bound yet; §9.4). */
  readonly binds: boolean;
}

const STATS: readonly GearStat[] = [GearStat.Damage, GearStat.Range, GearStat.FireRate, GearStat.Hp, GearStat.Points];
/** PlayerProfile.canWear's refusal when the owner's level is below the item's requirement. */
const LEVEL_TOO_LOW = "level too low";
const statOf = (b: GearBonus, s: GearStat): Fix64 => [b.damage, b.range, b.fireRate, b.hp, b.points][s];

/** Compares against the profile's own inventory. */
export function compareOn(profile: PlayerProfile, item: GearItem, owner: GearOwner): GearCompare {
  return compareWithin(profile, profile.gear, item, owner);
}

/**
 * Compares against `items` — the profile's inventory, or a copy of it part-way through a plan (`bestMoves`). Items are
 * matched by id; the returned items are the profile's own.
 */
export function compareWithin(profile: PlayerProfile, items: readonly GearItem[], item: GearItem, owner: GearOwner): GearCompare {
  const B = profile.balance;
  const real = (it: GearItem): GearItem => profile.findGear(it.id) ?? it;
  const self = items.find((g) => g.id === item.id) ?? item;
  const at = self.equippedTo;
  const wearing = at !== null && sameOwner(at, owner);
  const wornNow = wearing ? null : items.find((g) => g.slot === self.slot && g.equippedTo !== null && sameOwner(g.equippedTo, owner)) ?? null;
  const onOwner: GearOwner = { target: owner.target, towerIndex: owner.towerIndex };
  const swapped = items.map((g) => (g.id === self.id ? { ...g, equippedTo: onOwner } : wornNow !== null && g.id === wornNow.id ? { ...g, equippedTo: null } : g));
  const before = wearing ? bonusFor(B, items.map((g) => (g.id === self.id ? { ...g, equippedTo: null } : g)), owner) : bonusFor(B, items, owner);
  const after = bonusFor(B, swapped, owner);
  const source = at !== null && !wearing ? at : null;
  const canWear = profile.canWear(self.id, owner);
  const zero = Fix64.Zero;
  const mythicOf = (it: GearItem | null): MythicOn | null => (it === null || it.mythicEffect < 0 ? null : { effect: it.mythicEffect, active: mythicActiveOn(B, it, owner) });
  return {
    item: real(self), owner, worn: wornNow === null ? null : real(wornNow), wearing, canWear,
    needLevel: canWear === LEVEL_TOO_LOW ? self.level : null,
    before, after,
    stats: STATS.map((stat) => ({ stat, before: statOf(before, stat), after: statOf(after, stat) })),
    primary: { stat: primaryStatOn(B, self, owner), before: wornNow === null ? zero : primaryOn(B, wornNow, owner), after: primaryOn(B, self, owner), affinity: affinityOf(B, owner) === primaryStatOn(B, self, owner) },
    setBefore: before.setBonus, setAfter: after.setBonus,
    mythic: mythicOf(self), mythicLost: mythicOf(wornNow),
    power: itemScore(B, self) - (wornNow === null ? 0 : itemScore(B, wornNow)),
    takesFrom: source,
    takesFromBreaksSet: source !== null && bonusFor(B, items, source).setBonus && !bonusFor(B, swapped, source).setBonus,
    toStash: wornNow === null ? null : real(wornNow),
    binds: !self.bound && self.raised > B.gear.bindAboveRaised,
  };
}

/**
 * Puts an item on an owner in one tap: takes it off wherever it is and takes the owner's current item in that slot
 * off to the stash, then equips it — all through the profile's rules, and only once they allow it (a refusal changes
 * nothing). True when it is on the owner afterwards.
 */
export function equipOn(profile: PlayerProfile, itemId: number, owner: GearOwner): true | string {
  const fits = profile.canWear(itemId, owner);
  if (fits !== true) return fits;
  const it = profile.findGear(itemId)!;
  if (it.equippedTo !== null && sameOwner(it.equippedTo, owner)) return true;
  const worn = profile.equippedIn(owner, it.slot);
  if (it.equippedTo !== null) profile.unequipGear(it.id);
  if (worn !== null) profile.unequipGear(worn.id);
  return profile.equipGear(it.id, owner);
}
